import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import Anchor from "../anchors/Anchor";

interface CustomSelectProps {
    value: string | undefined | null;
    options: string[];
    onChange: (value: string, index: number) => void;
    width?: string;
    allowCustom?: boolean;
}

const SelectBox = styled.div`
    position: relative;
    display: inline-block;
`;

const Selected = styled.div`
    background: transparent;
    color: #f2f2f2;
    border-bottom: #f2f2f2 solid 1px;
    padding: .5rem .2rem;
    margin-top: .2rem;
    cursor: pointer;
    user-select: none;
    font-size: 1.1rem;

    span {
        display: inline-block;
        padding: .2rem .5rem;
    }

    span:last-child {
        float: right;
    }
`;

const OptionsList = styled.ul<{ open: boolean }>`
    background: var(--content-box-bg);
    border: 1px solid var(--content-box-border);
    margin: 0;
    padding: 0;
    list-style: none;
    z-index: 100;
    max-height: 10rem;
    overflow-y: auto;
    display: ${props => props.open ? "block" : "none"};
`;

const OptionItem = styled.li`
    padding: .5rem;
    color: #f2f2f2;
    user-select: none;
    cursor: pointer;
    &:hover, &:focus {
        background: var(--content-box-border);
    }
`;

const CustomInput = styled.input`
    background: transparent;
    color: #f2f2f2;
    border: none;
    font-size: 1.1rem;
    padding: .2rem .5rem;
    outline: none;
    width: calc(100% - 3rem);
`;

export function CustomSelect({
                                 value,
                                 options,
                                 onChange,
                                 width = '15rem',
                                 allowCustom = false
                             }: CustomSelectProps) {
    const [open, setOpen] = useState(false);
    const [customMode, setCustomMode] = useState(false);
    const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!allowCustom) return;
        if (!options.includes(value ?? '') && value !== undefined && value !== null) {
            setCustomMode(true);
        } else {
            setShouldAutoFocus(false);
            setCustomMode(false);
        }
    }, [value, options, allowCustom, customMode]);

    const handleSelect = (val: string, index: number) => {
        if (allowCustom && index === -1) {
            setCustomMode(true);
            setOpen(false);
            onChange(val, -1);
        } else {
            setCustomMode(false);
            onChange(val, index);
            setOpen(false);
        }
    };

    const handleCustomInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value, -1);
    };

    return (
        <SelectBox style={{ width: width }} tabIndex={0} onBlur={() => setOpen(false)}>
            <Selected onClick={() => setOpen(o => !o)}>
                {allowCustom && customMode ? (
                    <CustomInput
                        autoFocus={shouldAutoFocus}
                        value={value ?? ''}
                        onClick={(event) => event.stopPropagation()}
                        onChange={handleCustomInput}
                        placeholder="Custom value"
                    />
                ) : (
                    <span style={{ width: 'calc(100% - 3rem)', minHeight: '1.54rem' }}>
                        {/* {value.charAt(0).toUpperCase() + value.slice(1)} */}
                        {value ?? 'Select an option'}
                    </span>
                )}
                <span><i className="fa-solid fa-angle-down"></i></span>
            </Selected>
            <div ref={ref}></div>
            <Anchor open={open} anchorRef={ref}>
                <OptionsList style={{ width: width }} open={open}>
                    {options.map((opt, index) => (
                        <OptionItem
                            key={opt}
                            onMouseDown={() => handleSelect(opt, index)}
                            tabIndex={0}
                        >
                            {/*{opt.charAt(0).toUpperCase() + opt.slice(1)}*/}
                            {opt}
                        </OptionItem>
                    ))}
                    {allowCustom && (
                        <OptionItem
                            key="__custom__"
                            onMouseDown={() => {
                                setShouldAutoFocus(true);
                                handleSelect("", -1)
                            }}
                            tabIndex={0}
                        >
                            Custom
                        </OptionItem>
                    )}
                </OptionsList>
            </Anchor>
        </SelectBox>
    );
}